import { BadgeCheck, Database, Timer } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AddressPicker from '../components/AddressPicker';
import type { CompareState } from '../components/AddressPicker';
import AnalysisProgress from '../components/AnalysisProgress';
import ErrorPanel from '../components/ErrorPanel';
import Notice from '../components/Notice';
import PostcodeForm from '../components/PostcodeForm';
import RecentReports from '../components/RecentReports';
import Spinner from '../components/Spinner';
import { useComparison } from '../hooks/ComparisonContext';
import { useTour } from '../hooks/TourContext';
import { api } from '../lib/api';
import { tx } from '../i18n';
import { rememberVisit } from '../lib/saved';
import { rememberAnalysisKey } from '../lib/session';
import type { AddressOption, AddressSearchResponse } from '../types/api';

/**
 * The whole free journey: postcode, then address, then the report.
 *
 * <p>Two steps rather than one because an address-level product cannot start from a postcode
 * (acceptance criterion AC02). The second step is where the promise of the product is made good.
 */
export default function HomePage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState<AddressSearchResponse | null>(null);
  const [searching, setSearching] = useState(false);
  const [analysing, setAnalysing] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [lastPostcode, setLastPostcode] = useState('');
  const [pendingCompare, setPendingCompare] = useState<Record<string, 'adding' | 'removing'>>({});
  const { comparison, add, remove, rowFor, limitReached, clearLimitFlag } = useComparison();
  const paymentsRequired = comparison?.paymentsRequired ?? true;

  /*
   * The tour of this screen, shown once.
   *
   * Three steps, because on a first visit there are only three things here worth pointing at: the
   * box, what happens after it, and the fact that all of this exists in five languages. The
   * screen's real content - the address list, the report - is not on it yet, and a tour that
   * explains things that are not there is a tour that teaches nothing. The report has its own.
   */
  useTour({
    id: 'home',
    ready: true,
    steps: [
      {
        anchor: 'postcode',
        title: tx(
          'Start with the postcode',
          'Posta koduyla başlayın',
          'Zacznij od kodu pocztowego',
          'Comece pelo código postal',
          'Empieza por el código postal',
        ),
        body: tx(
          'The one on the listing is enough. We list the individual properties in it, so you pick the exact flat rather than the street.',
          'İlandaki posta kodu yeterli. İçindeki konutları tek tek listeleriz; böylece sokağı değil, tam daireyi seçersiniz.',
          'Wystarczy ten z ogłoszenia. Wypiszemy poszczególne mieszkania pod nim, więc wybierzesz konkretny lokal, a nie ulicę.',
          'O do anúncio é suficiente. Listamos os imóveis que existem nele, por isso escolhe o apartamento exato e não a rua.',
          'El del anuncio es suficiente. Listamos las viviendas que hay en él, así eliges el piso exacto y no la calle.',
        ),
      },
      {
        anchor: 'how-it-works',
        title: tx(
          'Then the property, then the report',
          'Sonra konut, sonra rapor',
          'Potem mieszkanie, potem raport',
          'Depois o imóvel, depois o relatório',
          'Luego la vivienda y después el informe',
        ),
        body: tx(
          'Three steps to a free report you can read before the viewing. Nothing here needs an account.',
          'Ev gezisinden önce okuyabileceğiniz ücretsiz bir rapora üç adım. Burada hiçbir şey için hesap gerekmez.',
          'Trzy kroki do bezpłatnego raportu, który przeczytasz przed oglądaniem. Nic tu nie wymaga konta.',
          'Três passos até um relatório gratuito que pode ler antes da visita. Nada aqui exige conta.',
          'Tres pasos hasta un informe gratuito que puedes leer antes de la visita. Nada de esto requiere cuenta.',
        ),
      },
      {
        anchor: 'language',
        title: tx('Five languages', 'Beş dil', 'Pięć języków', 'Cinco idiomas', 'Cinco idiomas'),
        body: tx(
          'The whole interface and every sentence of every report, in English, Turkish, Polish, Portuguese or Spanish. Your choice stays in this browser.',
          'Tüm arayüz ve her raporun her cümlesi; İngilizce, Türkçe, Lehçe, Portekizce ya da İspanyolca. Seçiminiz bu tarayıcıda kalır.',
          'Cały interfejs i każde zdanie każdego raportu — po angielsku, turecku, polsku, portugalsku lub hiszpańsku. Wybór zostaje w tej przeglądarce.',
          'Toda a interface e cada frase de cada relatório, em inglês, turco, polaco, português ou espanhol. A sua escolha fica neste navegador.',
          'Toda la interfaz y cada frase de cada informe, en inglés, turco, polaco, portugués o español. Tu elección se queda en este navegador.',
        ),
      },
    ],
  });

  const handlePostcode = (postcode: string) => {
    setLastPostcode(postcode);
    setSearching(true);
    setError(null);
    setSearch(null);

    api
      .addresses(postcode)
      .then(setSearch)
      .catch(setError)
      .finally(() => setSearching(false));
  };

  const handleAddress = (address: AddressOption) => {
    if (!search) {
      return;
    }
    setAnalysing(address.addressLine);
    setError(null);

    api
      .createAnalysis({
        postcode: search.postcode,
        uprn: address.uprn,
        addressLine: address.addressLine,
      })
      .then((summary) => {
        rememberAnalysisKey(summary.analysisId, summary.accessKey);
        rememberVisit(summary);
        navigate(`/report/${summary.analysisId}`);
      })
      .catch((cause: unknown) => {
        setError(cause);
        setAnalysing(null);
      });
  };

  const setPending = (addressLine: string, state: 'adding' | 'removing' | null) => {
    setPendingCompare((current) => {
      const next = { ...current };
      if (state === null) {
        delete next[addressLine];
      } else {
        next[addressLine] = state;
      }
      return next;
    });
  };

  /**
   * Adds a home to the comparison straight from the address list. The home is assessed first
   * (the comparison shows its free summary), and its report key is kept so it can be opened later.
   */
  const handleToggleCompare = async (address: AddressOption) => {
    if (!search) {
      return;
    }
    setError(null);
    clearLimitFlag();

    const existing = rowFor(search.postcode, address.addressLine);
    if (existing) {
      setPending(address.addressLine, 'removing');
      await remove(existing.propertyAddressId);
      setPending(address.addressLine, null);
      return;
    }

    setPending(address.addressLine, 'adding');
    try {
      const summary = await api.createAnalysis({
        postcode: search.postcode,
        uprn: address.uprn,
        addressLine: address.addressLine,
      });
      rememberAnalysisKey(summary.analysisId, summary.accessKey);
      rememberVisit(summary);
      await add(summary.propertyAddressId, summary.analysisId);
    } catch (cause) {
      setError(cause);
    } finally {
      setPending(address.addressLine, null);
    }
  };

  const compareState = (address: AddressOption): CompareState => {
    const pending = pendingCompare[address.addressLine];
    if (pending) {
      return pending;
    }
    return search && rowFor(search.postcode, address.addressLine) ? 'added' : 'idle';
  };

  return (
    <>
      <section className="hero">
        <div className="hero__scene" aria-hidden="true" />
        <div className="hero__inner">
          <p className="hero__eyebrow">
            <BadgeCheck size={14} aria-hidden="true" />
            {/* Not "open government data": most of it is, and Street View is not. A claim about
                provenance is exactly the kind this product cannot afford to round off. */}
            {tx('Built on UK public and licensed data', 'Birleşik Krallık kamu ve lisanslı verisiyle',
    'Oparte na brytyjskich danych publicznych i licencjonowanych',
    'Construído com dados públicos e licenciados do Reino Unido',
    'Construido con datos públicos y con licencia del Reino Unido')}
          </p>

          <h1 className="hero__title">
            {tx('Know the home', 'Konutu tanıyın',
    'Poznaj mieszkanie',
    'Conheça a casa',
    'Conoce la vivienda')}
            <span>{tx('before you sign.', 'imzalamadan önce.',
    'zanim podpiszesz.',
    'antes de assinar.',
    'antes de firmar.')}</span>
          </h1>

          <p className="hero__lede">
            {tx(
              'Pick the exact flat, not just the street. Safety, energy, broadband, council tax, transport and schools, each explained in a sentence.',
              'Sadece sokağı değil, tam daireyi seçin. Güvenlik, enerji, internet, council tax, ulaşım ve okullar; her biri tek cümleyle.',
    'Wybierz konkretne mieszkanie, a nie tylko ulicę. Bezpieczeństwo, energia, internet, podatek lokalny, komunikacja i szkoły — każde wyjaśnione jednym zdaniem.',
    'Escolha o apartamento exato, não apenas a rua. Segurança, energia, internet, imposto municipal, transportes e escolas, cada um explicado numa frase.',
    'Elige el piso exacto, no solo la calle. Seguridad, energía, internet, impuesto municipal, transporte y colegios, cada uno explicado en una frase.',
            )}
          </p>

          <div className="hero__card" data-tour="postcode">
            <p className="hero__card-label">{tx('Check an address', 'Bir adresi inceleyin',
    'Sprawdź adres',
    'Consultar uma morada',
    'Consultar una dirección')}</p>
            <PostcodeForm onSubmit={handlePostcode} loading={searching} initialValue={lastPostcode} />
          </div>
        </div>
      </section>

      <section className="page hero__facts-band">
        <dl className="grid grid--3">
          <Fact
            icon={<Timer size={16} aria-hidden="true" />}
            term={tx(
              'Check a property in seconds, not after the viewing',
              'Konutu ev gezisinden sonra değil, saniyeler içinde kontrol edin',
              'Sprawdź mieszkanie w kilka sekund, a nie po obejrzeniu',
              'Verifique um imóvel em segundos, não depois da visita',
              'Consulta una vivienda en segundos, no después de la visita',
            )}
          >
            {tx(
              'Public datasets checked at once, while you are still at the viewing.',
              'Kamu verileri tek seferde kontrol edilir; siz daha ev gezisindeyken.',
    'Publiczne zbiory danych sprawdzane naraz, jeszcze podczas oglądania mieszkania.',
    'Conjuntos de dados públicos consultados de uma vez, enquanto ainda está na visita.',
    'Conjuntos de datos públicos consultados a la vez, mientras sigues en la visita.',
            )}
          </Fact>
          <Fact icon={<Database size={16} aria-hidden="true" />} term={tx('Objective, not opinion', 'Görüş değil, veri',
    'Fakty, nie opinie',
    'Factos, não opiniões',
    'Datos, no opiniones')}>
            {tx(
              'Police, EPC and Ofcom data, scored with one published formula for every home.',
              'Polis, EPC ve Ofcom verileri; her konut için aynı açık formülle puanlanır.',
    'Dane policji, EPC i Ofcom, oceniane jednym opublikowanym wzorem dla każdego mieszkania.',
    'Dados da polícia, do EPC e da Ofcom, pontuados com uma fórmula publicada igual para todas as casas.',
    'Datos de la policía, del EPC y de Ofcom, puntuados con una fórmula publicada igual para toda vivienda.',
            )}
          </Fact>
          <Fact icon={<BadgeCheck size={16} aria-hidden="true" />} term={tx('Evidence you can show', 'Gösterebileceğiniz kanıt',
    'Dowody, które możesz pokazać',
    'Provas que pode mostrar',
    'Pruebas que puedes enseñar')}>
            {tx(
              'Every number carries its source and its date, and says what it cannot tell you.',
              'Her rakam kaynağını ve tarihini taşır, neyi söyleyemeyeceğini de belirtir.',
    'Każda liczba ma swoje źródło i datę oraz mówi, czego nie jest w stanie powiedzieć.',
    'Cada número traz a sua fonte e a sua data, e diz o que não consegue dizer-lhe.',
    'Cada cifra lleva su fuente y su fecha, y dice lo que no puede decirte.',
            )}
          </Fact>
        </dl>
      </section>

      <div className="page stack">
        {error ? (
          <ErrorPanel cause={error} onRetry={lastPostcode ? () => handlePostcode(lastPostcode) : undefined} />
        ) : null}

        {searching ? (
          <div className="card loading-panel">
            <Spinner label={tx('Looking up addresses', 'Adresler aranıyor',
    'Wyszukiwanie adresów',
    'A procurar moradas',
    'Buscando direcciones')} />
          </div>
        ) : null}

        {analysing ? <AnalysisProgress addressLine={analysing} /> : null}

        {limitReached ? (
          <Notice
            tone="warning"
            title={tx(
              `Your ${comparison?.freeLimit ?? 3} free comparison slots are full`,
              `${comparison?.freeLimit ?? 3} ücretsiz karşılaştırma hakkınız doldu`,
    `Wykorzystano wszystkie ${comparison?.freeLimit ?? 3} bezpłatne porównania`,
    `As suas ${comparison?.freeLimit ?? 3} comparações gratuitas estão esgotadas`,
    `Tus ${comparison?.freeLimit ?? 3} comparaciones gratuitas están agotadas`,
            )}
          >
            {tx(
              'Remove a home from your comparison, or unlock one, to add another. ',
              'Yenisini eklemek için karşılaştırmadan bir konut çıkarın ya da birinin kilidini açın. ',
    'Usuń mieszkanie z porównania lub odblokuj jedno, aby dodać kolejne. ',
    'Remova uma casa da comparação, ou desbloqueie uma, para adicionar outra. ',
    'Quita una vivienda de la comparación, o desbloquea una, para añadir otra. ',
            )}
            <Link to="/compare">{tx('Open your comparison', 'Karşılaştırmayı aç',
    'Otwórz porównanie',
    'Abrir a comparação',
    'Abrir la comparación')}</Link>
          </Notice>
        ) : null}

        {/*
          * Keyed, so React keeps the same picker rather than replacing it.
          *
          * These conditional siblings change the child index when one of them appears — the
          * "comparison is full" notice above is a null one moment and an element the next — and
          * an unkeyed picker at a shifted index is unmounted and mounted again. That throws away
          * its "show more" state and re-runs its mount effects, so adding a home to the
          * comparison scrolled the reader back up to the top of the list they were working
          * through. A key ties the element to its identity rather than to its position.
          */}
        {!searching && !analysing && search ? (
          <AddressPicker
            key="address-picker"
            postcode={search.postcode}
            borough={search.borough}
            addresses={search.addresses}
            onOpenReport={handleAddress}
            onToggleCompare={(address) => {
              void handleToggleCompare(address);
            }}
            compareState={compareState}
            busyAddressLine={analysing}
            onChangePostcode={() => {
              // Back to step one with the postcode still in the box, and the box in view.
              setSearch(null);
              setError(null);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        ) : null}

        {!searching && !analysing && !search ? (
          <>
            <RecentReports />
            <HowItWorks paymentsRequired={paymentsRequired} />
          </>
        ) : null}
      </div>
    </>
  );
}

function Fact({
  icon,
  term,
  children,
}: {
  icon: React.ReactNode;
  term: string;
  children: React.ReactNode;
}) {
  return (
    <div className="fact">
      <dt className="fact__term">
        {icon}
        {term}
      </dt>
      <dd className="fact__detail" style={{ margin: 0 }}>
        {children}
      </dd>
    </div>
  );
}

function HowItWorks({ paymentsRequired }: { paymentsRequired: boolean }) {
  const steps = [
    {
      title: tx('Enter the postcode', 'Posta kodunu girin',
    'Wpisz kod pocztowy',
    'Introduza o código postal',
    'Introduce el código postal'),
      body: tx(
        'The one on the listing is enough. We list the individual properties in it.',
        'İlandaki posta kodu yeterli. İçindeki konutları tek tek listeleriz.',
    'Wystarczy ten z ogłoszenia. Wypiszemy poszczególne mieszkania pod nim.',
    'O do anúncio é suficiente. Listamos os imóveis que existem nele.',
    'El del anuncio es suficiente. Listamos las viviendas que hay en él.',
      ),
    },
    {
      title: tx('Pick the exact property', 'Tam konutu seçin',
    'Wybierz konkretne mieszkanie',
    'Escolha o imóvel exato',
    'Elige la vivienda exacta'),
      body: tx(
        'Scores are about a specific home, not a vague sense of the area.',
        'Puanlar bölge hakkında belirsiz bir izlenim değil, belirli bir konut içindir.',
    'Oceny dotyczą konkretnego mieszkania, a nie ogólnego wrażenia o okolicy.',
    'As pontuações dizem respeito a uma casa concreta, não a uma ideia vaga da zona.',
    'Las puntuaciones son de una vivienda concreta, no de una impresión vaga del barrio.',
      ),
    },
    {
      title: tx('Read the report or compare', 'Raporu okuyun ya da karşılaştırın',
    'Przeczytaj raport albo porównaj',
    'Leia o relatório ou compare',
    'Lee el informe o compara'),
      body: tx(
        'Open the free report, or press Compare on a few homes and see them side by side.',
        'Ücretsiz raporu açın ya da birkaç konutta Karşılaştır düğmesine basıp yan yana görün.',
    'Otwórz bezpłatny raport albo dodaj kilka mieszkań do porównania i zobacz je obok siebie.',
    'Abra o relatório gratuito, ou carregue em Comparar em algumas casas e veja-as lado a lado.',
    'Abre el informe gratuito, o pulsa Comparar en varias viviendas y velas una al lado de otra.',
      ),
    },
    paymentsRequired
      ? {
          title: tx('Unlock what matters', 'Önemli olanın kilidini açın',
    'Odblokuj to, co ważne',
    'Desbloqueie o que importa',
    'Desbloquea lo que importa'),
          body: tx(
            '£1 for the evidence, the regional ranking and the PDF. Bundles for several viewings.',
            'Kanıtlar, bölge sıralaması ve PDF için £1. Birden çok ev gezisi için paketler.',
    '1 £ za dowody, ranking regionalny i PDF. Pakiety na kilka oglądań.',
    '1 £ pelas provas, o ranking regional e o PDF. Pacotes para várias visitas.',
    '1 £ por las pruebas, el ranking regional y el PDF. Packs para varias visitas.',
          ),
        }
      : {
          title: tx('Take the evidence with you', 'Kanıtları yanınızda götürün',
    'Zabierz dowody ze sobą',
    'Leve as provas consigo',
    'Llévate las pruebas contigo'),
          body: tx(
            'Every number with its source, the regional ranking and a one page PDF.',
            'Kaynağıyla her rakam, bölge sıralaması ve tek sayfalık PDF.',
    'Każda liczba ze źródłem, ranking regionalny i jednostronicowy PDF.',
    'Cada número com a sua fonte, o ranking regional e um PDF de uma página.',
    'Cada cifra con su fuente, el ranking regional y un PDF de una página.',
          ),
        },
  ];

  return (
    <section className="stack stack--tight" data-tour="how-it-works">
      <h2 className="card__title">{tx('How it works', 'Nasıl çalışır',
    'Jak to działa',
    'Como funciona',
    'Cómo funciona')}</h2>
      <ol className="steps">
        {steps.map((step, index) => (
          <li key={step.title} className="card">
            <span className="step__number">{index + 1}</span>
            <h3 className="card__title" style={{ marginTop: 12, fontSize: '0.95rem' }}>
              {step.title}
            </h3>
            <p className="small muted" style={{ marginTop: 4 }}>
              {step.body}
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}
